/**
 * Model Exporter
 * Main module for exporting 3D models to various formats
 * Provides unified interface for STL, PLY, and OBJ export
 */

import { meshToSTL, createSTLBlob } from './stlExporter.js';
import { meshToPLY, geometryToPLY, createPLYBlob } from './plyExporter.js';
import { meshToOBJExport, downloadOBJPackage } from './objExporter.js';
import { EXPORT_FORMATS, generateFileName } from '../exportConfig.js';
import { bakeTextureToVertexColors } from '../loaders/loaderUtils.js';
import {
  DEFAULT_EXPORT_VERTEX_UNIT,
  sceneCmToExportMultiplier,
} from '../exportUnits.js';

/** @deprecated Use {@link DEFAULT_EXPORT_VERTEX_UNIT} + {@link sceneCmToExportMultiplier}. */
export const EXPORT_VERTEX_SCALE_CM_TO_MM = sceneCmToExportMultiplier('mm');

/**
 * Clone mesh and scale geometry from cm scene space into export file units (default mm).
 *
 * @param {THREE.Mesh} mesh
 * @param {{ exportVertexUnit?: import('../exportUnits.js').ExportVertexUnit }} [options]
 */
export function createScaledExportMesh(
  mesh,
  { exportVertexUnit = DEFAULT_EXPORT_VERTEX_UNIT } = {}
) {
  const scaleFactor = sceneCmToExportMultiplier(exportVertexUnit);
  const exportMesh = mesh.clone(true);

  if (exportMesh.geometry?.clone) {
    exportMesh.geometry = exportMesh.geometry.clone();
  }

  if (scaleFactor !== 1 && exportMesh.geometry?.scale) {
    exportMesh.geometry.scale(scaleFactor, scaleFactor, scaleFactor);
    exportMesh.geometry.computeBoundingSphere?.();
    exportMesh.geometry.computeBoundingBox?.();
  }

  return exportMesh;
}

/**
 * Export Three.js mesh to specified format
 * @param {THREE.Mesh} mesh - Scene mesh (vertices in centimeters after load scale)
 * @param {Object} options
 *   - format: 'stl' | 'ply' | 'obj'
 *   - exportVertexUnit: 'mm' | 'cm' (default mm for manufacturing)
 *   - preserveColors, objStem, filename
 * @returns {Blob}
 */
export function exportMesh(mesh, options = {}) {
  const {
    format = EXPORT_FORMATS.STL,
    preserveColors = true,
    objStem = 'socket',
    exportVertexUnit = DEFAULT_EXPORT_VERTEX_UNIT,
  } = options;

  if (!mesh || !mesh.geometry) {
    throw new Error('Invalid mesh provided for export');
  }

  const exportScaleFactor = sceneCmToExportMultiplier(exportVertexUnit);
  let data = null;
  let blob = null;
  const scaledMesh = createScaledExportMesh(mesh, { exportVertexUnit });

  try {
    switch (format) {
      case EXPORT_FORMATS.STL:
        data = meshToSTL(scaledMesh);
        blob = createSTLBlob(data);
        break;

      case EXPORT_FORMATS.PLY:
        if (preserveColors) {
          const geom = mesh.geometry;
          const hasColor = geom?.hasAttribute?.('color');
          const hasUV = geom?.hasAttribute?.('uv');
          const mat = mesh.material;
          const map = Array.isArray(mat) ? mat?.[0]?.map : mat?.map;
          if (!hasColor && hasUV && map?.image && map.image.complete) {
            const cloned = geom.clone();
            const baked = bakeTextureToVertexColors(cloned, map);
            if (baked) cloned.setAttribute('color', baked);
            mesh.updateWorldMatrix(true, false);
            cloned.applyMatrix4(mesh.matrixWorld);
            if (exportScaleFactor !== 1 && cloned.scale) {
              cloned.scale(exportScaleFactor, exportScaleFactor, exportScaleFactor);
              cloned.computeBoundingSphere?.();
              cloned.computeBoundingBox?.();
            }
            data = geometryToPLY(cloned, { preserveColors });
            blob = createPLYBlob(data);
            break;
          }
        }
        data = meshToPLY(scaledMesh, { preserveColors });
        blob = createPLYBlob(data);
        break;

      case EXPORT_FORMATS.OBJ: {
        const { objText } = meshToOBJExport(scaledMesh, objStem);
        blob = new Blob([objText], { type: 'model/obj' });
        break;
      }

      default:
        throw new Error(`Unsupported export format: ${format}`);
    }

    return blob;
  } catch (error) {
    console.error(`Error exporting mesh to ${format.toUpperCase()}:`, error);
    throw error;
  }
}

/**
 * Export mesh and trigger download
 * @param {THREE.Mesh} mesh
 * @param {Object} options
 */
export function downloadMesh(mesh, options = {}) {
  const {
    format = EXPORT_FORMATS.STL,
    filename = generateFileName('socket', format),
    preserveColors = true,
    exportVertexUnit = DEFAULT_EXPORT_VERTEX_UNIT,
  } = options;

  try {
    if (format === EXPORT_FORMATS.OBJ) {
      const stem = filename.replace(/\.(obj|stl|ply|mtl)$/i, '') || 'socket';
      const scaledMesh = createScaledExportMesh(mesh, { exportVertexUnit });
      downloadOBJPackage(scaledMesh, stem);
      return;
    }
    const blob = exportMesh(mesh, { format, preserveColors, exportVertexUnit });
    downloadBlob(blob, filename);
  } catch (error) {
    console.error('Error downloading mesh:', error);
    throw error;
  }
}

/**
 * @param {THREE.Mesh} mesh
 * @param {Object} options
 * @returns {string}
 */
export function exportMeshAsDataURL(mesh, options = {}) {
  try {
    const blob = exportMesh(mesh, options);
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error creating data URL:', error);
    throw error;
  }
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function getSupportedFormats() {
  return EXPORT_FORMATS;
}
